import React from 'react';
import { useTranslation } from 'next-i18next';
import classnames from 'classnames';

import { Fade } from 'components/animations';
import { Icon, Input } from 'components/ui';
import { TRANSLATIONS } from 'constants/translations';

import styles from './RecipesSearchBox.module.scss';

interface Props {
  className?: string,
  initialQuery?: string | null,
  onSearch?: (query: string, initialQuery?: string | null) => void,
  onClear?: () => void
}

/*
  TODO - standardize search components across entire application (combine this with SearchBox.js)
*/

const RecipesSearchBox = (props: Props) => {

  const {
    className,
    initialQuery,
    onSearch, onClear
  } = props;

  const [query, setQuery] = React.useState('');

  const { t } = useTranslation();

  React.useEffect(() => {
    setQuery(initialQuery || '');
  }, [initialQuery]);

  const handleClearInput = () => {
    setQuery('');
    if (onClear) {
      onClear();
    }
  };

  const handleEnterKeydown = (e: KeyboardEvent) => {
    e.preventDefault();
    handleSearch();
  };

  const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    handleSearch();
  };

  const handleSearch = () => {
    if (onSearch) {
      onSearch(query, initialQuery);
    }
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e?.target?.value || '');
  };

  const isClearButtonVisible = !!query;
  const isSearchButtonActive = query ? query !== initialQuery : (Boolean(query) !== Boolean(initialQuery));

  return (
    <div className={classnames(styles.root, className)}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <Input
          // TODO remove when Input is migrated to TS
          // @ts-ignore
          withoutForm
          value={query}
          type="text"
          placeholder={t('SEARCH.PLACEHOLDER', { ns: TRANSLATIONS.RECIPES })}
          inputClassName={styles.input}
          onEnter={handleEnterKeydown}
          onChange={handleQueryChange}
        />

        {/* Search button */}
        <SearchButton isActive={isSearchButtonActive} onClick={handleSearch} />

        {/* Clear button */}
        <ClearButton isVisible={isClearButtonVisible} onClick={handleClearInput} />
      </form>
    </div>
  );
};

// Search Button
const SearchButton = ({ onClick, isActive }: {onClick: () => void, isActive?: boolean}) => (
  <div className={styles.searchButtonContainer}>
    <div
      className={classnames(styles.searchButtonWrapper, { [styles.active]: isActive })}
      onClick={onClick}
    >
      <Icon
        className={styles.searchButton}
        name="search"
        size={18}
        strokeWidth={2}
      />
    </div>
  </div>
);

// Clear Button

const ClearButton = (props: {isVisible?: boolean, onClick: () => void}) => {
  const { isVisible, onClick } = props;
  return (
    <Fade
      className={classnames(
        styles.clearButtonContainer,
        { [styles.inactive]: !isVisible },
      )}
      isVisible={!!isVisible}
      enterDuration={300}
      enterDelay={300}
      enterScale={0.5}
      exitDuration={200}
      exitDelay={0}
      exitScale={0.85}
      persist
    >
      <div
        className={styles.clearButtonWrapper}
        onClick={onClick}
      >
        <Icon
          className={styles.clearButton}
          name="x"
          size={18}
          strokeWidth={2}
        />
      </div>
    </Fade>
  );
};

export default RecipesSearchBox;
